import { cn } from "@/lib/utils";
import { AXIS_KEYS, AXIS_LABEL, AXIS_HIGHLIGHT_BG } from "@/constants/ab-tests";
import { getBulletIndex } from "@/utils/ab-test-utils";
import type { ContentType, HeroAsinWithIntent, ParsedField } from "@/types/ab-tests";

interface VariantAPaneProps {
  selectedFields: ContentType[];
  heroAsin: HeroAsinWithIntent;
  parsedZonesA: Record<string, ParsedField | null>;
  showColorCoding?: boolean;
}

export function VariantAPane({ selectedFields, heroAsin, parsedZonesA, showColorCoding = true }: VariantAPaneProps) {
  return (
    <div className="w-[360px] shrink-0 px-6 pb-6 overflow-y-auto h-full">
      <div className="sticky top-0 z-20 -mx-6 px-6 pt-6 pb-3 bg-card shadow-[0_4px_8px_-6px_rgba(0,0,0,0.08)]">
        <h2 className="text-2xl font-semibold text-foreground">Variant A</h2>
        <p className="text-xs text-muted-foreground mt-1">Current value of the listing field you&apos;re testing.</p>
      </div>

      <div className="space-y-4 mt-3">
        {selectedFields.map(field => {
          const isTitle = field === "title";
          const idx = getBulletIndex(field);
          const label = isTitle ? "TITLE" : `BULLET ${(idx ?? 0) + 1}`;
          const fieldText = isTitle ? heroAsin.title : (heroAsin.bulletPoints?.[idx ?? 0] ?? "");
          const zonesA = parsedZonesA[field];
          const charCount = fieldText.length;
          const charLimit = isTitle ? 200 : 500;

          return (
            <div key={field} className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
              <div className="rounded-md border border-border bg-muted/50 px-3 py-2.5 min-h-[80px]">
                {showColorCoding && zonesA && zonesA.zones && zonesA.zones.length > 0 ? (
                  <p className="text-xs leading-relaxed text-foreground">
                    {zonesA.zones.map((z, zi) => {
                      const trailingSpace = zi < zonesA.zones.length - 1 ? " " : "";
                      if (z.kind === "product-name") {
                        if (z.nameSegments && z.nameSegments.length > 0) {
                          return (
                            <span key={z.id}>
                              {z.nameSegments.map((seg, si) => (
                                <span key={`${z.id}-s${si}`}>
                                  <span
                                    className={cn(
                                      "rounded-sm px-0.5 py-px font-semibold",
                                      seg.axis ? AXIS_HIGHLIGHT_BG[seg.axis] : "text-foreground",
                                    )}
                                    title={seg.axis ? AXIS_LABEL[seg.axis] : undefined}
                                  >
                                    {seg.text}
                                  </span>
                                  {si < z.nameSegments!.length - 1 ? " " : ""}
                                </span>
                              ))}
                              {trailingSpace}
                            </span>
                          );
                        }
                        return (
                          <span key={z.id} className="font-semibold text-foreground">
                            {z.text}{trailingSpace}
                          </span>
                        );
                      }
                      const bg = z.axis ? (AXIS_HIGHLIGHT_BG[z.axis] ?? "") : "";
                      const title = z.axis ? AXIS_LABEL[z.axis] : undefined;
                      return (
                        <span key={z.id} className={cn("rounded-sm px-0.5 py-px", bg)} title={title}>
                          {z.text}{trailingSpace}
                        </span>
                      );
                    })}
                  </p>
                ) : (
                  <p className="text-xs leading-relaxed text-foreground whitespace-pre-wrap">
                    {fieldText || (
                      <span className="text-muted-foreground italic">
                        {isTitle ? "No content" : "No current bullet"}
                      </span>
                    )}
                  </p>
                )}
              </div>
              <p className={cn(
                "text-[10px] text-right",
                charCount > charLimit ? "text-destructive font-medium" : "text-muted-foreground",
              )}>
                {charCount} / {charLimit}
              </p>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pt-1">
        {AXIS_KEYS.map(axis => (
          <div key={axis} className="flex items-center gap-1.5">
            <span className={cn("h-2.5 w-2.5 rounded-sm", AXIS_HIGHLIGHT_BG[axis])} />
            <span className="text-[11px] text-muted-foreground">{AXIS_LABEL[axis]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
